// src/contexts/UserContext.jsx
import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { clearLocalStorage, decodeJwt, isJwtExpired, startSessionTimer } from '../utilities/utilities';
import { api } from '../utilities/api';
import { clearPaymentAction } from '../utilities/paymentAction';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const isActivatedComplete = (activated) => {
  if (activated === true || activated === 1 || activated === '1') return true;
  if (activated === false || activated == null || activated === 0 || activated === '0') return false;
  return String(activated).toLowerCase() === 'true';
};

const isRegistrationOnlyJwt = (jwt) => {
  const claims = decodeJwt(jwt);
  return Boolean(
    claims
    && claims.token_use === 'registration'
    && ['contact_otp', 'email_otp'].includes(claims.auth_level)
    && claims.scope === 'registration:continue'
  );
};

const hasUsableStoredJwt = () => {
  const jwt = localStorage.getItem('redimdwm_jwt');
  return Boolean(jwt && !isJwtExpired(jwt) && !isRegistrationOnlyJwt(jwt));
};

export const UserProvider = ({ children }) => {
  const LOCAL_KEY_PREFIX = 'redimdwm_';
  const [currentUserId, setCurrentUserId] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loadingSession, setLoadingSession] = useState(hasUsableStoredJwt);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');

  const completeAuthenticatedSession = async (result, missingJwtMessage) => {
    if (result.error) throw new Error(result.error);
    if (!result.jwt) throw new Error(missingJwtMessage);

    clearPaymentAction();
    localStorage.setItem(LOCAL_KEY_PREFIX+'jwt', result.jwt);
    localStorage.setItem(LOCAL_KEY_PREFIX+'userid', result.userid);
    localStorage.setItem(LOCAL_KEY_PREFIX+'user_name', result.user_name || result.email || '');
    localStorage.setItem(LOCAL_KEY_PREFIX+'email', result.email);
    localStorage.setItem(LOCAL_KEY_PREFIX+'firstname', result.firstname);
    localStorage.setItem(LOCAL_KEY_PREFIX+'lastname', result.lastname);
    localStorage.setItem(LOCAL_KEY_PREFIX+'activated', isActivatedComplete(result.activated) ? '1' : '0');

    const authUser = {
      jwt: result.jwt,
      userid: result.userid,
      user_name: result.user_name || result.email || '',
      email: result.email,
      firstname: result.firstname,
      lastname: result.lastname,
      activated: isActivatedComplete(result.activated) ? '1' : '0',
    };

    setUser(authUser);

    const refresh = await refreshUserInfo();
    if (refresh.success) {
      startSessionTimer(decodeJwt(result.jwt).exp - Math.floor(Date.now() / 1000));
      setLoadingSession(false);
      setIsLoggedIn(isActivatedComplete(refresh.profile?.activated ?? authUser.activated));
      return { success: true, user: authUser, profile: refresh.profile };
    }

    setError(refresh.error);
    setUser(null);
    setIsLoggedIn(false);
    clearLocalStorage();
    setLoadingSession(false);
    return { success: false, error: refresh.error };
  };

  // user login
  const login = async (credentials) => {
    setError(null);
    setIsLoggedIn(false);
    setLoadingSession(true);

    try {
      const result = await api.login(credentials);
      return await completeAuthenticatedSession(result, "Login did not return a JWT");

    } catch (err) {
      const msg = err.message || 'Login failed';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoadingSession(false);
    }
  };

  const passkeyLogin = async (assertionData) => {
    setError(null);
    setIsLoggedIn(false);
    setLoadingSession(true);

    try {
      const result = await api.verifyPasskeyLogin(assertionData);
      return await completeAuthenticatedSession(result, "Passkey sign-in did not return a JWT");
    } catch (err) {
      const msg = err.message || 'Passkey sign-in failed';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoadingSession(false);
    }
  };

  // logout user
  const logout = (silent = false) => {
    clearPaymentAction();
    setIsLoggedIn(false);
    setUser(null);
    setLoadingSession(false);
    clearLocalStorage();

    if (window.clearSessionTimer) {
      window.clearSessionTimer();
    }
    
    if (!silent) window.location.href = '/';

  };

  // Fetch full user info and merge into current state using the userid in the jwt
  const refreshUserInfo = async () => {
    setError(null);
    setLoadingSession(true);

    try {
      const profile = await api.getUserInfo();
      if (profile?.success) {
        setCurrentUserId(profile.userid);

        // Apply gender-based defaults for missing health_profile data
        const applyHealthDefaults = (data) => {
          const gender = data?.gender?.toLowerCase();

          // Only apply defaults if health_profile data is missing
          const defaults = {};

          if (!data.height) {
            defaults.height = gender === 'female' ? 62 : gender === 'male' ? 69 : 66;
            defaults._heightIsDefault = true;
          }

          if (!data.weight) {
            defaults.weight = gender === 'female' ? 165 : gender === 'male' ? 200 : 180;
            defaults._weightIsDefault = true;
          }

          if (!data.goal_weight && defaults.weight) {
            defaults.goal_weight = Math.max(100, defaults.weight - 20);
            defaults._goalWeightIsDefault = true;
          }

          return { ...data, ...defaults };
        };

        const dataWithDefaults = applyHealthDefaults(profile);

        // Sanitize numeric fields that legacy rows can return as date strings
        // (e.g. health_profiles.bmi was declared as DATE; old rows arrive as
        // "0000-00-00"). Treat anything that isn't a clean number as null.
        const toNumberOrNull = (v) => {
          if (v == null || v === '') return null;
          const s = String(v).trim();
          if (/^0000-00-00/.test(s)) return null;
          const n = parseFloat(s);
          return Number.isFinite(n) ? n : null;
        };

        const processedData = {
          ...dataWithDefaults,
          weight: toNumberOrNull(dataWithDefaults?.weight),
          goal_weight: toNumberOrNull(dataWithDefaults?.goal_weight),
          bmi: toNumberOrNull(dataWithDefaults?.bmi),
          start_date: dataWithDefaults?.start_date && dataWithDefaults.start_date !== '0000-00-00' ? dataWithDefaults.start_date : null,
          goal_date: dataWithDefaults?.goal_date && dataWithDefaults.goal_date !== '0000-00-00' ? dataWithDefaults.goal_date : null,
        };

        setUser(prev => ({ ...prev, ...processedData }));
        setLoadingSession(false);
        return { success: true, profile: processedData };
      }

      const errorMessage = profile?.error || 'Unknown error';
      setLoadingSession(false);
      
      return { success: false, error: errorMessage };

    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch full profile';
      setLoadingSession(false);
      return { success: false, error: errorMessage };
    }
  };

  // validate jwt
  const checkSession = async () => {
    setLoadingSession(true);
    try {
      const result = await api.checkJwt();
      if (!result?.logged_in || !result.jwt || !result.userid) {
        logout(true);
        return;
      }

      const decoded = decodeJwt(result.jwt);
      startSessionTimer(decoded.exp - Math.floor(Date.now() / 1000));
      localStorage.setItem(LOCAL_KEY_PREFIX+'jwt', result.jwt);
      localStorage.setItem(LOCAL_KEY_PREFIX+'user_name', result.user_name || result.email || '');

      setUser(prev => ({
        ...prev,
        jwt: result.jwt,
        userid: result.userid,
        user_name: result.user_name || result.email || '',
        email: result.email,
        firstname: result.firstname,
        lastname: result.lastname,
        activated: isActivatedComplete(result.activated) ? '1' : '0',
      }));

      const refresh = await refreshUserInfo(); // ⬅️ same logic here
      if (refresh.success) {
        setIsLoggedIn(isActivatedComplete(refresh.profile?.activated ?? result.activated));
      } else {
        setUser(null);
        setIsLoggedIn(false);
        clearLocalStorage();
      }
    } catch {
      logout(true);
    } finally {
      setLoadingSession(false);
    }
  };

  // update user demographics
  const updateUserInfo = async (userData) => {
    try {
      const res = await api.updateUserInfo(userData);
      if (res.success) {
        const refreshed = await refreshUserInfo();
        return {
          ...(refreshed && typeof refreshed === 'object' ? refreshed : {}),
          success: true,
          verification_sent: res.verification_sent || [],
          message: res.message || null,
        };
      }
      throw new Error(res.error || 'Failed to update user info');

    } catch (err) {
      setError(err?.message || 'Failed to update user info');
      return false;
    }
  };

  // update user medical history
  const updateHealthProfile = async (healthProfileData) => {
    try {
      const res = await api.updateHealthInfo(healthProfileData);
      if (res.success) return await refreshUserInfo();
      throw new Error(res.error || 'Failed to update health profile');
    
    } catch (err) {
      setError(err?.message || 'Failed to update health profile');
      return false;
    }
  };

  useEffect(() => {
    const jwt = localStorage.getItem('redimdwm_jwt');
    if (jwt) {
      if (isJwtExpired(jwt)) {
        localStorage.removeItem('redimdwm_jwt');
      } else if (isRegistrationOnlyJwt(jwt)) {
        // This token may continue only the preactivation workflow. It must
        // never bootstrap a normal app session or unlock protected routes.
        setLoadingSession(false);
        setIsLoggedIn(false);
      } else {
        const decoded = decodeJwt(jwt);
        startSessionTimer(decoded.exp - Math.floor(Date.now() / 1000));
        checkSession();
      }
    }
  }, []);

  useEffect(() => {
    const jwt = localStorage.getItem('redimdwm_jwt');
    if(!isJwtExpired(jwt) && isActivatedComplete(user?.activated)) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  },[user?.activated]);

  const contextValue = useMemo(() => ({
    login,
    passkeyLogin,
    logout,
    refreshUserInfo,
    checkSession,
    updateUserInfo,
    updateHealthProfile,
    loadingSession,
    isLoggedIn,
    currentUserId,
    user,
    setUser,         // optional but flexible
    error,
    setError,
    success,
    setSuccess,
  }), [isLoggedIn, loadingSession, currentUserId, user, error, success ]);

return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};
